import * as Sentry from "@sentry/browser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
    type Company,
    type Permissions,
    ResponseError,
    type UserMembershipSettings,
} from "../apiClient/generated";
import { useAccountsApiClient, useAuthApiClient } from "../hooks";
import { ROUTES } from "../routes";

export interface UserState {
    id: number;
    // Properties
    email: string;
    firstName: string;
    lastName: string;
    geofenceAreas: number[];
    company: Company;
    // Feature flags
    flags: string[];
    // Permissions & settings
    isStaff: boolean;
    isTemporaryUser: boolean;
    viewAsCompany: boolean;
    isSuperuser: boolean;
    permissions: Permissions;
    settings: UserMembershipSettings;
}

/**
 * Holds the loaded user. Only ever populated by <AuthProvider>, which does not
 * render its children until the session check has resolved, so anything below
 * it can treat the user as always present.
 */
export const UserContext = createContext<UserState | null>(null);

/**
 * Ends the session, tears down every trace of the user and sends them to the
 * login page. Safe to call when already logged out.
 */
export const useLogout = () => {
    const queryClient = useQueryClient();
    const authApiClient = useAuthApiClient();
    const navigate = useNavigate();

    const logoutMutation = useMutation({
        mutationFn: async () => {
            try {
                await authApiClient.authLogoutCreate();
            } catch (error) {
                // Ignore 401 Unauthorized errors, the user is already logged out.
                if (
                    !(error instanceof ResponseError) ||
                    error.response.status !== 401
                ) {
                    Sentry.captureException(error);
                }
            }
        },
        // Runs on success AND failure
        onSettled: async () => {
            Sentry.setUser(null);
            await queryClient.cancelQueries();
            queryClient.clear();
            // Clear everything but the cookie acceptance.
            const cookiesAccepted = localStorage.getItem("cookiesAccepted");
            localStorage.clear();
            if (cookiesAccepted) {
                localStorage.setItem("cookiesAccepted", cookiesAccepted);
            }
            // Redirect to login screen
            navigate(ROUTES.LOGIN);
        },
    });

    return logoutMutation.mutateAsync;
};

/**
 * Session lifecycle: the state of the auth check itself, plus logout.
 *
 * Most components want useUser() instead. Use this only where the *lifecycle*
 * matters: the provider and the login pages.
 */
export const useAuth = () => {
    const apiClient = useAccountsApiClient();
    const logout = useLogout();

    const authStateQuery = useQuery({
        queryKey: ["auth"],
        queryFn: async (): Promise<UserState> => {
            const [membership, company] = await Promise.all([
                apiClient.accountsMembershipMyRetrieve(),
                apiClient.accountsCompaniesMyRetrieve(),
            ]);

            Sentry.setUser({
                id: membership.user.id,
                flags: membership.flags.join(", "),
                settings: membership.settings,
                isStaff: membership.isStaff,
                isSuperuser: membership.isSuperuser,
            });

            return {
                company,
                id: membership.user.id,
                // Properties
                email: membership.user.email,
                firstName: membership.user.firstName,
                lastName: membership.user.lastName,
                geofenceAreas: membership.user.geofenceAreas,
                // Feature flags
                flags: membership.flags,
                // Permissions & settings
                isStaff: membership.isStaff,
                isSuperuser: membership.isSuperuser,
                isTemporaryUser: membership.isTemporaryUser,
                viewAsCompany: membership.viewAsCompany,
                permissions: membership.permissions,
                settings: membership.settings,
            };
        },
        // In 6 hours this will check again.
        staleTime: 6 * 3600 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        // Session expiry is owned by the API client middleware, which redirects
        // to the login page on any 401. No point retrying here.
        retry: false,
    });

    return {
        loading: authStateQuery.isLoading,
        // True while the session is being re-checked after a previous failure,
        // which isLoading does not cover: react-query v4 keeps status "error"
        // through an invalidate-driven refetch.
        rechecking: authStateQuery.isFetching,
        loggedIn: authStateQuery.isSuccess,
        error: authStateQuery.isError,
        userState: authStateQuery.data,
        logout,
        // Re-runs the session check and resolves with the result, so callers
        // can tell a fresh session from a failed one.
        refresh: authStateQuery.refetch,
    };
};

/**
 * The logged in user. Throws outside <AuthProvider>, which is what makes the
 * return type non-optional at every call site.
 */
export const useUser = () => {
    const user = useContext(UserContext);
    if (!user) {
        throw new Error("useUser must be used within <AuthProvider>");
    }
    return user;
};

export const useHasFlag = (flag: string) => useUser().flags.includes(flag);

export const usePermission = (permission: keyof Permissions) =>
    useUser().permissions[permission];
